/**
 * Kleine Bausteine der Mail-Werkstatt: Rückfrage-Dialog, Wortvergleich,
 * Art-Etikett und relative Zeitangabe.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, CircleCheckBig, MessageSquare, PencilLine, RotateCcw, Trash2, XCircle } from 'lucide-react';
import { Modal } from '../Modal/Modal';
import { Avatar } from '../Planung/Avatar';
import { wannText, type FeedbackArt, type FeedbackEintrag, type WerkstattAdapter } from '../../lib/planung';
import type { DiffTeil } from '../../lib/zusammenarbeit';
import { ART_LABEL, datumLang, zurueckziehbar, type WerkstattAktionen } from './werkstattHilfen';

type RueckfrageProps = {
  offen: boolean;
  titel: string;
  text: React.ReactNode;
  ja: string;
  nein?: string;
  gefaehrlich?: boolean;
  laeuft?: boolean;
  onJa: () => void;
  onNein: () => void;
  /** Schließen (×, Esc) — ohne: wie „Nein“ */
  onSchliessen?: () => void;
};

/** Ja/Nein-Dialog im Look der App (statt window.confirm); hängt am <body>, damit nichts darüber liegt. */
export function Rueckfrage({ offen, titel, text, ja, nein = 'Abbrechen', gefaehrlich, laeuft, onJa, onNein, onSchliessen }: RueckfrageProps) {
  if (!offen || typeof document === 'undefined') return null;
  return createPortal(
    <Modal isOpen={offen} onClose={onSchliessen ?? onNein} title={titel} size="sm">
      <div className="za-rueckfrage">
        <div className="za-rueckfrage-text">{text}</div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onNein} disabled={laeuft}>{nein}</button>
          <button
            type="button"
            className={`btn ${gefaehrlich ? 'btn-danger' : 'btn-primary'}`}
            onClick={onJa}
            disabled={laeuft}
            autoFocus
          >
            {laeuft && <span className="spinner za-spinner-klein" aria-hidden="true" />}
            {ja}
          </button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
}

/** Wortvergleich: gestrichen rot, neu grün. */
export function DiffText({ teile, leer = '(leer)' }: { teile: DiffTeil[]; leer?: string }) {
  if (teile.length === 0) return <span className="za-diff-leer">{leer}</span>;
  return (
    <>
      {teile.map((t, i) => {
        if (t.typ === 'weg') return <del key={i} className="za-diff-weg">{t.text}</del>;
        if (t.typ === 'neu') return <ins key={i} className="za-diff-neu">{t.text}</ins>;
        return <span key={i}>{t.text}</span>;
      })}
    </>
  );
}

const ART_ICON: Record<FeedbackArt, typeof MessageSquare> = {
  kommentar: MessageSquare,
  vorschlag: PencilLine,
  passt: CheckCircle2,
};

export function ArtBadge({ art }: { art: FeedbackArt }) {
  const Icon = ART_ICON[art];
  return (
    <span className={`za-art za-art--${art}`}>
      <Icon size={12} aria-hidden="true" />
      {ART_LABEL[art]}
    </span>
  );
}

/** „vor 5 Min." — aktualisiert sich jede Minute; das volle Datum steht im Tooltip. */
export function Zeit({ iso }: { iso: string }) {
  const [, setTakt] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTakt((x) => x + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <time className="za-zeit" dateTime={iso} title={datumLang(iso)}>
      {wannText(iso)}
    </time>
  );
}

/** Kopf einer Karte: wer, wann, welche Art. */
export function EintragKopf({ eintrag, adapter, klein }: { eintrag: FeedbackEintrag; adapter: WerkstattAdapter; klein?: boolean }) {
  return (
    <header className={`za-kopf${klein ? ' za-kopf--klein' : ''}`}>
      <Avatar name={eintrag.autor_name} groesse={klein ? 'xs' : 'sm'} />
      <div className="za-kopf-wer">
        <span className="za-kopf-zeile">
          <span className="za-kopf-name">{eintrag.autor_name}</span>
          {eintrag.ist_meins && <span className="za-kopf-du">(Du)</span>}
          {eintrag.autor_art === 'team' && <span className="za-rolle">Team</span>}
          {klein && <Zeit iso={eintrag.created_at} />}
        </span>
        {!klein && (
          <span className="za-kopf-zeile za-kopf-zeile--meta">
            <Zeit iso={eintrag.created_at} />
            <ArtBadge art={eintrag.art} />
          </span>
        )}
      </div>
      {!klein && adapter.art === 'team' && !eintrag.gelesen && eintrag.autor_art !== 'team' && (
        <span className="za-neu" title="Neu — noch nicht angesehen">neu</span>
      )}
    </header>
  );
}

const ERGEBNIS: Record<NonNullable<FeedbackEintrag['ergebnis']>, { label: string; Icon: typeof CircleCheckBig }> = {
  erledigt: { label: 'Erledigt', Icon: CircleCheckBig },
  uebernommen: { label: 'Übernommen', Icon: CheckCircle2 },
  abgelehnt: { label: 'Abgelehnt', Icon: XCircle },
};

/** „Erledigt von Renee · vor 5 Min." */
export function EintragStatus({ eintrag }: { eintrag: FeedbackEintrag }) {
  if (!eintrag.ergebnis) return null;
  const { label, Icon } = ERGEBNIS[eintrag.ergebnis];
  return (
    <p className={`za-ergebnis za-ergebnis--${eintrag.ergebnis}`}>
      <Icon size={13} aria-hidden="true" />
      <span>
        {label}
        {eintrag.erledigt_von_name && <> von {eintrag.erledigt_von_name}</>}
        {eintrag.erledigt_am && <> · <Zeit iso={eintrag.erledigt_am} /></>}
      </span>
    </p>
  );
}

/**
 * Erledigt / Wieder öffnen (Team) und Zurückziehen (eigene Einträge, solange
 * niemand sonst geantwortet und das Team nichts entschieden hat). `alle`: alle
 * Einträge der Mail (für die Antworten).
 */
export function EintragAktionen({ eintrag, alle, adapter, aktionen, children }: {
  eintrag: FeedbackEintrag; alle: FeedbackEintrag[]; adapter: WerkstattAdapter; aktionen: WerkstattAktionen; children?: React.ReactNode;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const team = adapter.darfEntscheiden && !!adapter.entscheiden;
  const zurueck = !!adapter.zurueckziehen && zurueckziehbar(eintrag, alle);
  if (!team && !zurueck && !children) return null;
  const erledigen = async (wieder: boolean) => {
    setLaeuft(true);
    try { await aktionen.erledigen(eintrag, wieder); } finally { setLaeuft(false); }
  };
  return (
    <div className="za-aktionen">
      {children}
      {team && !eintrag.ergebnis && eintrag.art === 'kommentar' && (
        <button type="button" className="btn btn-ghost btn-sm za-knopf-erledigt" onClick={(e) => { e.stopPropagation(); void erledigen(false); }} disabled={laeuft}>
          <CircleCheckBig size={14} aria-hidden="true" /> Erledigt
        </button>
      )}
      {team && eintrag.ergebnis && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); void erledigen(true); }} disabled={laeuft}>
          <RotateCcw size={14} aria-hidden="true" /> Wieder öffnen
        </button>
      )}
      {zurueck && (
        <button type="button" className="btn btn-ghost btn-sm za-knopf-zurueck" onClick={(e) => { e.stopPropagation(); aktionen.zurueckziehen(eintrag); }}>
          <Trash2 size={14} aria-hidden="true" /> Zurückziehen
        </button>
      )}
    </div>
  );
}
